/**
 * Typed accessors over the canonical eCTD backbone sections generated from
 * the official EMA/ICH DTDs (see ./dtd/SOURCES.md and ectdSections.generated.ts).
 */
import { references } from "../reference";
import {
  generatedEctdSections,
  GeneratedEctdSection,
} from "./ectdSections.generated";

export type EctdSection = GeneratedEctdSection & {
  /**
   * ectdKey of the reference document in `references` that covers this
   * section (longest prefix match, same rule as runPromptsForFiles),
   * or undefined when no reference text exists.
   */
  referenceKey?: string;
};

const referenceKeys = references
  .map((r) => r.ectdKey)
  .filter((key) => key && key !== "pi-doc");

function resolveReferenceKey(sectionKey: string): string | undefined {
  let best: string | undefined;
  referenceKeys.forEach((refKey) => {
    if (
      (sectionKey === refKey || sectionKey.startsWith(refKey)) &&
      (!best || refKey.length > best.length)
    ) {
      best = refKey;
    }
  });
  return best;
}

export const ectdSections: EctdSection[] = generatedEctdSections.map(
  (section) => ({
    ...section,
    referenceKey: resolveReferenceKey(section.key),
  })
);

const sectionsByKey = new Map<string, EctdSection>(
  ectdSections.map((s) => [s.key, s])
);

// First occurrence wins (ICH document order first, then EU M1). Module 3+
// codes are unique; only shallow module-root codes can collide.
const sectionsByCode = new Map<string, EctdSection>();
ectdSections.forEach((s) => {
  if (!sectionsByCode.has(s.code)) sectionsByCode.set(s.code, s);
});

export function findSectionByKey(key: string): EctdSection | undefined {
  return sectionsByKey.get(key);
}

export function findSectionByCode(code: string): EctdSection | undefined {
  return sectionsByCode.get(code);
}

/**
 * Normalize a raw section code found in a document ("3.2. P.4.2", "3.2.S.3.",
 * "3.2.R:") to a canonical known code. Unknown trailing segments are clamped
 * to the deepest known ancestor ("3.2.P.3.4.1" -> "3.2.P.3.4").
 * Returns null when no known section matches.
 */
export function normalizeSectionCode(raw: string): string | null {
  const cleaned = raw
    .toUpperCase()
    .replace(/\s+/g, "")
    .replace(/[.:;,]+$/g, "");
  if (!cleaned) return null;
  const segments = cleaned.split(".").filter((s) => s.length > 0);
  while (segments.length > 0) {
    const code = segments.join(".");
    if (sectionsByCode.has(code)) return code;
    segments.pop();
  }
  return null;
}

/**
 * The outermost repeatable ancestor (including the section itself), e.g.
 * `m3-2-s-drug-substance` for 3.2.S.4.2. Nested repeatable subtrees are
 * folded into their enclosing variant: 3.2.P.4 (Control of Excipients) is
 * repeatable per excipient in the DTD, but its files belong to the drug
 * product they describe, so 3.2.P.4.x resolves to `m3-2-p-drug-product`.
 * Sections outside any repeatable subtree (3.2.A, 3.2.R, 3.3) return null.
 */
export function getVariantRoot(section: EctdSection): EctdSection | null {
  let outermost: EctdSection | null = null;
  let current: EctdSection | undefined = section;
  while (current) {
    if (current.repeatable) outermost = current;
    current = current.parentKey
      ? sectionsByKey.get(current.parentKey)
      : undefined;
  }
  return outermost;
}

/** All ancestors from the module root down to the direct parent. */
export function getAncestors(section: EctdSection): EctdSection[] {
  const ancestors: EctdSection[] = [];
  let current = section.parentKey
    ? sectionsByKey.get(section.parentKey)
    : undefined;
  while (current) {
    ancestors.unshift(current);
    current = current.parentKey
      ? sectionsByKey.get(current.parentKey)
      : undefined;
  }
  return ancestors;
}

const KEYWORD_STOPWORDS = new Set([
  "of",
  "the",
  "and",
  "or",
  "a",
  "an",
  "for",
  "to",
  "in",
  "on",
  "its",
]);

/** Lower-cased significant title words used for filename matching. */
export function titleKeywords(section: EctdSection): string[] {
  return section.title
    .toLowerCase()
    .split(/[^a-z]+/)
    .filter((word) => word.length > 1 && !KEYWORD_STOPWORDS.has(word));
}

export function sectionsForModule(moduleNumber: number): EctdSection[] {
  return ectdSections.filter((s) => s.module === moduleNumber);
}

/** True when `descendant` is inside the subtree rooted at `ancestor` (strict). */
export function isDescendantOf(
  descendant: EctdSection,
  ancestor: EctdSection
): boolean {
  let current = descendant.parentKey
    ? sectionsByKey.get(descendant.parentKey)
    : undefined;
  while (current) {
    if (current.key === ancestor.key) return true;
    current = current.parentKey
      ? sectionsByKey.get(current.parentKey)
      : undefined;
  }
  return false;
}
